/**
 * IndexedDB cache for chat messages.
 *
 * Design:
 * - All data is scoped to the authenticated userId (multi-account safe).
 * - Message content is obfuscated via a lightweight XOR cipher keyed on userId
 *   (not cryptographic-grade, but prevents casual inspection of the DB).
 * - On logout the entire DB is destroyed.
 *
 * Stores:
 *   "messages"  — individual messages, key = `${userId}:${msg.id}`
 *   "meta"      — per-chat sync metadata (lastTimestamp, page, hasMore)
 */

import FEATURE_FLAGS from "../constants/featureFlags";
import searchIndex from "./searchIndex";

const DB_NAME = "efone-message-cache";
const MSG_STORE = "messages";
const META_STORE = "meta";

// ── Lightweight XOR obfuscation ─────────────────────────────────────────────
function xorCipher(text: string, key: string): string {
  if (!key) return text;
  let result = "";
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(
      text.charCodeAt(i) ^ key.charCodeAt(i % key.length),
    );
  }
  return result;
}

function obfuscate(value: string, userId: string): string {
  // encodeURIComponent converts all non-ASCII chars (emoji, etc.) to %-escaped
  // ASCII sequences so btoa never encounters characters > 255.
  return btoa(xorCipher(encodeURIComponent(value), userId));
}

function deobfuscate(encoded: string, userId: string): string {
  try {
    return decodeURIComponent(xorCipher(atob(encoded), userId));
  } catch {
    return encoded; // graceful fallback (old cache format or corruption)
  }
}

const DB_VERSION = 2; // bumped for sequenceId index

// ── DB open / upgrade ───────────────────────────────────────────────────────
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = req.result;
      if (!db.objectStoreNames.contains(MSG_STORE)) {
        const store = db.createObjectStore(MSG_STORE, { keyPath: "key" });
        store.createIndex("chatKey", "chatKey", { unique: false });
        store.createIndex("createdAt", "createdAt", { unique: false });
        store.createIndex("chatKeySeq", ["chatKey", "sequenceId"], {
          unique: false,
        });
      } else if (event.oldVersion < 2) {
        // Migration: add chatKeySeq compound index to existing store
        const tx = (event.target as IDBOpenDBRequest).transaction!;
        const store = tx.objectStore(MSG_STORE);
        if (!store.indexNames.contains("chatKeySeq")) {
          store.createIndex("chatKeySeq", ["chatKey", "sequenceId"], {
            unique: false,
          });
        }
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: "key" });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── Typed helpers ───────────────────────────────────────────────────────────
interface CachedMessage {
  key: string; // `${userId}:${msg.id}`
  chatKey: string; // `${userId}:${chatId}`
  data: string; // obfuscated JSON
  createdAt: string; // ISO string for ordering
  sequenceId?: number; // optional monotonic sequence for cursor-based pagination
}

interface ChatMeta {
  key: string; // `${userId}:${chatId}`
  lastTimestamp: string; // ISO string of newest message
  lastMessageId: string;
  hasMore: boolean;
  cachedAt: number; // Date.now()
}

// ── Public API ──────────────────────────────────────────────────────────────
const messageCacheDB = {
  /**
   * Store an array of formatted messages for a chat.
   * Existing messages with the same ID are overwritten (upsert).
   */
  async putMessages(
    userId: string,
    chatId: string,
    messages: any[],
  ): Promise<void> {
    if (!messages.length) return;
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(MSG_STORE, "readwrite");
      const store = tx.objectStore(MSG_STORE);
      const chatKey = `${userId}:${chatId}`;

      for (let i = 0; i < messages.length; i++) {
        const msg = messages[i];
        if (!msg.id) continue;
        const entry: CachedMessage = {
          key: `${userId}:${msg.id}`,
          chatKey,
          data: obfuscate(JSON.stringify(msg), userId),
          createdAt: msg.createdAt || new Date().toISOString(),
          sequenceId: msg.sequenceId ?? Date.now() + i,
        };
        store.put(entry);
      }

      tx.oncomplete = () => {
        db.close();
        if (FEATURE_FLAGS.USE_CRDT_ENGINE) {
          searchIndex.indexMessages(userId, chatId, messages).catch(() => {});
        }
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  },

  /**
   * Retrieve all cached messages for a chat, ordered by createdAt ascending.
   */
  async getMessages(userId: string, chatId: string): Promise<any[]> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(MSG_STORE, "readonly");
      const store = tx.objectStore(MSG_STORE);
      const index = store.index("chatKey");
      const chatKey = `${userId}:${chatId}`;
      const req = index.getAll(chatKey);

      req.onsuccess = () => {
        const entries: CachedMessage[] = req.result;
        const messages = entries
          .map((e) => {
            try {
              return JSON.parse(deobfuscate(e.data, userId));
            } catch {
              return null;
            }
          })
          .filter(Boolean)
          .sort(
            (a: any, b: any) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );
        resolve(messages);
      };
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },

  /**
   * O(1) lookup of a single message by its ID.
   * Returns the decoded message object or null if not cached.
   */
  async getMessage(
    userId: string,
    chatId: string,
    messageId: string,
  ): Promise<any | null> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(MSG_STORE, "readonly");
      const store = tx.objectStore(MSG_STORE);
      const req = store.get(`${userId}:${messageId}`);
      req.onsuccess = () => {
        if (!req.result) {
          resolve(null);
          return;
        }
        try {
          resolve(
            JSON.parse(deobfuscate((req.result as CachedMessage).data, userId)),
          );
        } catch {
          resolve(null);
        }
      };
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },

  /**
   * Return messages around a given ISO timestamp for jump-to-message context.
   * Fetches up to windowSize/2 messages before and after the timestamp.
   */
  async getMessagesAroundTimestamp(
    userId: string,
    chatId: string,
    timestamp: string,
    windowSize = 40,
  ): Promise<any[]> {
    const chatKey = `${userId}:${chatId}`;
    const half = Math.floor(windowSize / 2);
    const db = await openDB();

    const fetchRange = (
      range: IDBKeyRange,
      direction: IDBCursorDirection,
      limit: number,
    ): Promise<CachedMessage[]> =>
      new Promise((resolve, reject) => {
        const results: CachedMessage[] = [];
        const tx = db.transaction(MSG_STORE, "readonly");
        const index = tx.objectStore(MSG_STORE).index("createdAt");
        const req = index.openCursor(range, direction);
        req.onsuccess = () => {
          const cursor = req.result;
          if (cursor && results.length < limit) {
            const entry = cursor.value as CachedMessage;
            if (entry.chatKey === chatKey) results.push(entry);
            cursor.continue();
          } else {
            resolve(results);
          }
        };
        req.onerror = () => reject(req.error);
        tx.oncomplete = () => {};
      });

    const [before, after] = await Promise.all([
      fetchRange(IDBKeyRange.upperBound(timestamp, true), "prev", half),
      fetchRange(IDBKeyRange.lowerBound(timestamp, false), "next", half),
    ]);

    db.close();

    const decode = (e: CachedMessage) => {
      try {
        return JSON.parse(deobfuscate(e.data, userId));
      } catch {
        return null;
      }
    };

    return [...before.reverse(), ...after]
      .map(decode)
      .filter(Boolean)
      .sort(
        (a: any, b: any) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
  },

  /**
   * Remove a single message from cache.
   */
  async removeMessage(userId: string, messageId: string): Promise<void> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(MSG_STORE, "readwrite");
      const store = tx.objectStore(MSG_STORE);
      store.delete(`${userId}:${messageId}`);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  },

  /**
   * Remove all cached messages for a specific chat.
   */
  async clearChat(userId: string, chatId: string): Promise<void> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([MSG_STORE, META_STORE], "readwrite");
      const msgStore = tx.objectStore(MSG_STORE);
      const metaStore = tx.objectStore(META_STORE);
      const chatKey = `${userId}:${chatId}`;

      // Delete messages
      const index = msgStore.index("chatKey");
      const cursor = index.openCursor(chatKey);
      cursor.onsuccess = () => {
        const c = cursor.result;
        if (c) {
          c.delete();
          c.continue();
        }
      };

      // Delete meta
      metaStore.delete(chatKey);

      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  },

  // ── Sync metadata ─────────────────────────────────────────────────────────

  async getMeta(userId: string, chatId: string): Promise<ChatMeta | undefined> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(META_STORE, "readonly");
      const store = tx.objectStore(META_STORE);
      const req = store.get(`${userId}:${chatId}`);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },

  async putMeta(
    userId: string,
    chatId: string,
    meta: Omit<ChatMeta, "key">,
  ): Promise<void> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(META_STORE, "readwrite");
      const store = tx.objectStore(META_STORE);
      store.put({ ...meta, key: `${userId}:${chatId}` });
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  },

  /**
   * Return up to `limit` messages for a chat whose sequenceId is less than
   * `beforeSequenceId`. Falls back to createdAt-ordered full fetch when
   * rows have no sequenceId (pre-migration data).
   */
  async getChatMessagePage(
    userId: string,
    chatId: string,
    beforeSequenceId: number,
    limit = 20,
  ): Promise<any[]> {
    const chatKey = `${userId}:${chatId}`;
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(MSG_STORE, "readonly");
      const store = tx.objectStore(MSG_STORE);
      const results: CachedMessage[] = [];

      // Try compound index first
      if (store.indexNames.contains("chatKeySeq")) {
        // Range: chatKey matches AND sequenceId < beforeSequenceId
        const range = IDBKeyRange.bound(
          [chatKey, 0],
          [chatKey, beforeSequenceId - 1],
        );
        const req = store.index("chatKeySeq").openCursor(range, "prev");
        req.onsuccess = () => {
          const cursor = req.result;
          if (cursor && results.length < limit) {
            results.push(cursor.value as CachedMessage);
            cursor.continue();
          } else {
            const messages = results
              .reverse()
              .map((e) => {
                try {
                  return JSON.parse(deobfuscate(e.data, userId));
                } catch {
                  return null;
                }
              })
              .filter(Boolean);
            resolve(messages);
          }
        };
        req.onerror = () => reject(req.error);
      } else {
        // Fallback: no index available — return empty so caller fetches from API
        resolve([]);
      }

      tx.oncomplete = () => db.close();
    });
  },

  /**
   * Return up to `limit` messages for a chat with createdAt strictly before
   * `beforeCreatedAt`, ordered ascending. Uses the existing `createdAt` index —
   * no schema change needed.
   */
  async getMessagesBefore(
    userId: string,
    chatId: string,
    beforeCreatedAt: string,
    limit = 20,
  ): Promise<any[]> {
    const chatKey = `${userId}:${chatId}`;
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const results: CachedMessage[] = [];
      const tx = db.transaction(MSG_STORE, "readonly");
      const index = tx.objectStore(MSG_STORE).index("createdAt");
      const range = IDBKeyRange.upperBound(beforeCreatedAt, true); // exclusive
      const req = index.openCursor(range, "prev");
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor && results.length < limit) {
          const entry = cursor.value as CachedMessage;
          if (entry.chatKey === chatKey) results.push(entry);
          cursor.continue();
        } else {
          const messages = results
            .reverse()
            .map((e) => {
              try {
                return JSON.parse(deobfuscate(e.data, userId));
              } catch {
                return null;
              }
            })
            .filter(Boolean);
          resolve(messages);
        }
      };
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  },

  // ── Cleanup ───────────────────────────────────────────────────────────────

  /** Delete the entire database (called on logout). */
  async destroyAll(): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
      req.onblocked = () => resolve(); // resolve anyway
    });
  },

  /** Remove cached messages older than maxAge (default 7 days). */
  async cleanup(maxAge = 7 * 24 * 60 * 60 * 1000): Promise<void> {
    const cutoff = new Date(Date.now() - maxAge).toISOString();
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(MSG_STORE, "readwrite");
      const store = tx.objectStore(MSG_STORE);
      const index = store.index("createdAt");
      const range = IDBKeyRange.upperBound(cutoff);
      const cursor = index.openCursor(range);
      cursor.onsuccess = () => {
        const c = cursor.result;
        if (c) {
          c.delete();
          c.continue();
        }
      };
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  },
};

export default messageCacheDB;
